import { ITooltipModel } from "./tooltip-model.interface";
import { IServiceFactoryExtended } from "../../service-factory-extended.interface";
import { makeObservable, observable, runInAction } from "mobx";
import { DomEventSubscription } from "../../../../utils/event-listener-registration";
import { Check } from "@sparkware/app-utils";
import { createPopper } from "@popperjs/core";
import { flipTooltipModifier, offsetTooltipModifier, preventOverflowTooltipModifier } from "./tooltip-modifiers";
import React from "react";
import { namedSetTimeout } from "../../../../common/utils/time-functions";
import { v4 as uuidv4 } from "uuid";
import { ITooltipOptions, ITooltipStyling } from "./tooltip-options.interface";
import { TooltipTypeEnum } from "./tooltip-view-model.interface";

export abstract class TooltipBaseModel<TOptions extends ITooltipOptions> implements ITooltipModel {
    constructor(protected readonly services: IServiceFactoryExtended,
        protected readonly options: TOptions) {

        makeObservable(this);

        this.id = uuidv4();
    }

    private _subscribedToDocClickEventTimeoutRef: any = null;
    private _subscribedToDocScrollEventTimeoutRef: any = null;

    private _onDocumentClickHandler = (event: any) => {
        if (this.options.closeOnClickOutside && this.tooltipContainer && !this.tooltipContainer.contains(event.target)) {
            this.closeTooltip();
        }
    }

    private _onDocumentScrollHandler = () => {
        if (this.options.closeOnScroll) {
            this.closeTooltip();
        }
    }

    readonly id: string;
    get tooltipType(): TooltipTypeEnum {
        return this.options?.tooltipType ?? TooltipTypeEnum.Generic
    }

    private _domEventSubscription: DomEventSubscription[] = [];

    private _autoCloseTimeout: any = null;
    protected _popperInstance: any = null;

    protected abstract _getElementToAttach(): HTMLElement | null;


    @observable.ref
    tooltipContainer: HTMLElement | null;

    shouldDisplay(): boolean {
        return true;
    }


    onToolTipRendered(tooltipContainer: HTMLElement): void {

        runInAction(() => {
            this.tooltipContainer = tooltipContainer;
        });

        if (this.options.onTooltipRendered) {
            this.options.onTooltipRendered();
        }

        if (!Check.isNullOrUndefined(this.options.timeout)) {
            this._autoCloseTimeout = namedSetTimeout('TOOLTIP_CLOSE_TIMER', () => this.closeTooltip(), this.options.timeout.totalMilliseconds);
        }

        if (this.options.closeOnClickOutside) {
            // We use setTimeout because onTooltipRender is called when the element to attach is clicked. 
            // This would result in the tooltip closing immediately, so we delay the subscription 
            // to the document click event until the click event of the attached element is processed (and the tooltip has already appeared).
            this._subscribedToDocClickEventTimeoutRef = setTimeout(() => {
                this._domEventSubscription.push(this.services.dom.subscribeTo.click(this._onDocumentClickHandler));
                this._subscribedToDocClickEventTimeoutRef = null;
            }, 0);
        }

        if (this.options.closeOnScroll) {
            // We use setTimeout because onTooltipRender is called when the dom is scrolled. 
            // This would result in the tooltip closing immediately, so we delay the subscription 
            // to the document scroll event until the scroll event of the attached element is processed (and the tooltip has already appeared).
            this._subscribedToDocScrollEventTimeoutRef = setTimeout(() => {
                this._domEventSubscription.push(this.services.dom.subscribeTo.scroll(this._onDocumentScrollHandler));
                this._subscribedToDocScrollEventTimeoutRef = null;
            }, 0);
        }
    }

    get hasOwnStyle(): boolean {
        return Boolean(this.options.hasOwnStyle);
    }

    get zIndex(): number {
        if (Check.isNullOrUndefined(this.options.zIndex)) {
            return 203;
        }

        if (this.options.showOverlay) {
            return this.options.zIndex + 1;
        }

        return this.options.zIndex;
    }

    get animated(): boolean {
        if (Check.isNullOrUndefined(this.options.animated)) {
            return true;
        }

        return this.options.animated;
    }

    get hasGlow(): boolean {
        if (Check.isNullOrUndefined(this.options.hasGlow)) {
            return true;
        }

        return this.options.hasGlow;
    }

    get shouldShowCloseButton(): boolean {
        return Boolean(this.options.showCloseButton);
    }

    get invertCloseButtonColors(): boolean {
        return Boolean(this.options.invertCloseButtonColors);
    }


    get styling(): ITooltipStyling | undefined {
        return this.options.styling;
    }

    get showOverlay(): boolean {
        return Boolean(this.options.showOverlay);
    }

    private _createTooltipPopper(elementToAttach: HTMLElement) {

        return createPopper(elementToAttach, this.tooltipContainer, {
            placement: this.options.placement || 'auto',
            strategy: this.options.strategy || "fixed",
            modifiers: [
                preventOverflowTooltipModifier,
                flipTooltipModifier,
                offsetTooltipModifier,
                ...(this.options.modifiers || [])
            ]
        });
    }

    render(): string | React.ReactElement | null {
        if (!this.tooltipContainer) {
            return null;
        }

        let elementToAttach = this._getElementToAttach();
        if (!elementToAttach) {
            elementToAttach = this.services.dom.body;
        }

        if (this._popperInstance) {
            this._destroyPopperInstance();
        }

        this._popperInstance = this._createTooltipPopper(elementToAttach);

        return this.options.renderContent();

    }

    private _destroyPopperInstance(): void {
        if (this._popperInstance) {
            this._popperInstance.destroy();
            this._popperInstance = null;
        }
    }

    private _unsubscribeFromDomEvents(): void {
        if (this._subscribedToDocClickEventTimeoutRef) {
            clearTimeout(this._subscribedToDocClickEventTimeoutRef);
            this._subscribedToDocClickEventTimeoutRef = null;
        }

        if (this._subscribedToDocScrollEventTimeoutRef) {
            clearTimeout(this._subscribedToDocScrollEventTimeoutRef);
            this._subscribedToDocScrollEventTimeoutRef = null;
        }

        this._domEventSubscription.forEach(ev => ev.dispose());
        this._domEventSubscription = [];
    }

    private _clearAutoCloseTimeout(): void {
        if (this._autoCloseTimeout) {
            clearTimeout(this._autoCloseTimeout);
            this._autoCloseTimeout = null;
        }
    }

    private _dispose(): void {
        this._clearAutoCloseTimeout();

        this._destroyPopperInstance();

        this._unsubscribeFromDomEvents();

        runInAction(() => {
            this.tooltipContainer = null;
        });
    }

    closeTooltip(): void {
        this._dispose();

        if (this.options.onTooltipClosed) {
            this.options.onTooltipClosed();
        }
    }

}